import { Timestamp } from 'firebase/firestore';
import type { Address, StatusNote } from './shared';
import type { StatusTone } from '../components/shared/statusTone';

/**
 * Support Case - Multi-channel customer support tracking
 * Inspired by Zendesk/HubSpot case architecture
 */
export interface SupportCase {
  caseId: string;
  organizationId: string;

  // Human-readable case number (e.g., "CS2601-0001")
  caseNumber: string;

  // Core case info
  subject: string;
  status: CaseStatus;
  priority: CasePriority;
  type: CaseType;

  // Channel tracking - where did this case originate?
  channel: CaseChannel;
  via: CaseVia;

  // People
  requesterId?: string; // Org MEMBER id of an internal requester (server-checked); a customer is linked by contactId
  requesterName: string;
  requesterEmail?: string;
  requesterPhone?: string;

  submitterId: string; // User ID who created the case (agent or system)
  assigneeId?: string; // User ID of assigned agent
  assigneeName?: string; // Display name of assigned agent (denormalized)
  assigneeGroupId?: string; // Team/group assignment

  // Collaboration
  followerIds: string[]; // Internal users watching this case
  ccEmails: string[]; // External emails CC'd on replies

  // Classification
  tags: string[];

  // Linked entities (optional, for future expansion)
  contactId?: string;
  customerId?: string;
  equipmentId?: string;
  serviceTicketId?: string; // Links to P2 service tickets (field service)
  dealId?: string;
  leadId?: string; // CRM lead created from this case
  callLogId?: string; // Can be created from a call log
  triageOutcome?: TriageOutcome; // Triage decision for web form cases
  triageClassification?: TriageClassification; // AI classification result
  rfpRecordId?: string;   // Link to rfp_records document

  // Branch assignment
  branchId?: string; // References Branch.branchId from org settings

  // Location
  address?: Address; // Structured address with optional geocoding

  // Status notes (annotated notes with author/timestamp tracking)
  statusNotes?: StatusNote[];

  // Call log sync tracking (for cases created from call logs)
  callLogSyncEnabled?: boolean; // True = actively syncing from call log
  lastSyncedFromCallLog?: Timestamp;

  // SLA tracking
  firstResponseAt?: Timestamp;
  resolvedAt?: Timestamp;

  // Counts (denormalized for performance)
  messageCount: number;
  internalNoteCount: number;

  // Web form submission data (present when channel === 'web_form')
  formData?: Record<string, unknown>;

  // Drive folder for form attachments (present when form had file uploads)
  driveFolderUrl?: string;
  driveFolderId?: string;

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastActivityAt: Timestamp;
  lastCustomerReplyAt?: Timestamp;
  lastAgentReplyAt?: Timestamp;
}

/**
 * Case Message - Individual message in a case thread
 * Stored as a subcollection under cases
 */
export interface CaseMessage {
  messageId: string;
  caseId: string;

  // Message type
  type: MessageType;
  isPublic: boolean; // false = internal note, true = visible to customer

  // Content
  body: string;
  htmlBody?: string;

  // Author
  authorId: string;
  authorType: AuthorType;
  authorName: string;
  authorEmail?: string;

  // For email threading
  emailMessageId?: string; // Gmail message ID
  emailThreadId?: string; // Gmail thread ID
  inReplyTo?: string; // Previous message ID

  // Channel info (which channel was this sent through)
  channel: CaseChannel;

  // Attachments
  attachments: CaseAttachment[];

  // Mentions (@user in internal notes)
  mentions: string[]; // User IDs

  // Metadata
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  editedAt?: Timestamp;
  editedBy?: string;
}

/**
 * Case Attachment
 */
export interface CaseAttachment {
  attachmentId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  thumbnailUrl?: string;
}

/**
 * Channel origin tracking
 */
export interface CaseVia {
  source: CaseChannel;
  sourceId?: string; // e.g., Gmail message ID, form submission ID, Dialpad call ID
  inboxId?: string; // Which inbox received this (for email)
  inboxEmail?: string; // The email address that received this
  formId?: string; // For web form submissions
  webhookId?: string; // For external integrations
  /** Call recording URL (for phone channel) */
  recordingUrl?: string;
  /** Call transcript (for phone channel, if available) */
  transcript?: string;
}

// ============================================================================
// ENUMS AND CONSTANTS
// ============================================================================

export type CaseStatus =
  | 'new'
  | 'open'
  | 'pending' // Waiting on customer
  | 'on_hold' // Waiting on third party
  | 'solved'
  | 'closed';

export type CasePriority = 'low' | 'normal' | 'high' | 'urgent';

export type CaseType =
  | 'question'
  | 'problem'
  | 'incident'
  | 'task'
  | 'feature_request'
  | 'rfp';

export type CaseChannel =
  | 'email'
  | 'phone'
  | 'web_form'
  | 'chat'
  | 'internal'
  | 'api'
  | 'call_center';

export type MessageType =
  | 'customer_message'
  | 'agent_reply'
  | 'internal_note'
  | 'system_message'
  | 'status_change';

export type AuthorType = 'customer' | 'agent' | 'system';

/**
 * Triage outcome for web form cases — records what action was taken
 */
export type TriageOutcome =
  | 'converted_to_lead'   // Case converted to a CRM lead
  | 'resolved_directly'   // Resolved without creating a lead
  | 'spam'                // Marked as spam
  | 'duplicate'           // Duplicate submission
  | 'needs_followup';     // Requires follow-up but not converted yet

/**
 * AI triage classification category
 */
export type TriageCategory =
  | 'sales_lead'         // Potential sales opportunity
  | 'service_request'    // Existing customer needs service
  | 'vendor'             // Vendor solicitation / marketing
  | 'spam';              // Spam or junk

/**
 * AI triage classification result attached to web form cases
 */
export interface TriageClassification {
  category: TriageCategory;
  confidence: number; // 0-1
  reasoning?: string;
  matchedCustomer?: {
    customerId: string;
    customerName: string;
  };
  matchedContact?: {
    contactId: string;
    contactName: string;
  };
  error?: string; // If classification failed
}

// ============================================================================
// DISPLAY CONFIGURATION
// ============================================================================

export const TRIAGE_CATEGORY_CONFIG: Record<TriageCategory, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}> = {
  sales_lead: {
    label: 'Sales Lead',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    description: 'Potential sales opportunity',
  },
  service_request: {
    label: 'Service Request',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    description: 'Existing customer needs service',
  },
  vendor: {
    label: 'Vendor',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    description: 'Vendor solicitation',
  },
  spam: {
    label: 'Spam',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    description: 'Spam or junk submission',
  },
};

export const CASE_STATUS_CONFIG: Record<CaseStatus, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
  /** What the status MEANS, for `<StatusBadge tone>`. See `statusTone.ts`. */
  tone: StatusTone;
}> = {
  new: {
    label: 'New',
    tone: 'active',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    description: 'Case has not been viewed',
  },
  open: {
    label: 'Open',
    tone: 'attention',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    description: 'Case is being worked on',
  },
  pending: {
    label: 'Pending',
    tone: 'attention',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    description: 'Waiting for customer response',
  },
  on_hold: {
    label: 'On Hold',
    tone: 'attention',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    description: 'Waiting on third party',
  },
  solved: {
    label: 'Solved',
    tone: 'done',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    description: 'Issue has been resolved',
  },
  closed: {
    label: 'Closed',
    tone: 'neutral',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    description: 'Case is complete',
  },
};

export const CASE_PRIORITY_CONFIG: Record<CasePriority, {
  label: string;
  color: string;
  bgColor: string;
  /** `normal` is deliberately neutral: a badge that is always blue says nothing. */
  tone: StatusTone;
}> = {
  low: {
    label: 'Low',
    tone: 'neutral',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  normal: {
    label: 'Normal',
    tone: 'neutral',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  high: {
    label: 'High',
    tone: 'attention',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  urgent: {
    label: 'Urgent',
    tone: 'blocked',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};

export const CASE_TYPE_CONFIG: Record<CaseType, {
  label: string;
  icon: string;
}> = {
  question: { label: 'Question', icon: '?' },
  problem: { label: 'Problem', icon: '!' },
  incident: { label: 'Incident', icon: '⚠' },
  task: { label: 'Task', icon: '✓' },
  feature_request: { label: 'Feature Request', icon: '★' },
  rfp: { label: 'RFP', icon: '📋' },
};

export const CASE_CHANNEL_CONFIG: Record<CaseChannel, {
  label: string;
  icon: string;
}> = {
  email: { label: 'Email', icon: '✉' },
  phone: { label: 'Phone', icon: '☎' },
  web_form: { label: 'Web Form', icon: '📝' },
  chat: { label: 'Chat', icon: '💬' },
  internal: { label: 'Internal', icon: '🏢' },
  api: { label: 'API', icon: '🔌' },
  call_center: { label: 'Call Center', icon: '📞' },
};

// ============================================================================
// FILTER TYPES
// ============================================================================

export type CaseSortField = 'createdAt' | 'lastActivityAt' | 'priority' | 'status';
export type SortDirection = 'asc' | 'desc';

export interface SupportCaseFilters {
  status?: CaseStatus | CaseStatus[] | 'all';
  priority?: CasePriority | 'all';
  type?: CaseType | 'all';
  channel?: CaseChannel | 'all';
  assigneeId?: string | 'unassigned' | 'all';
  requesterId?: string;
  branchId?: string | 'all';
  tags?: string[];
  searchQuery?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  /** Filter for cases synced from call logs */
  hasCallLog?: boolean;
  /** Sort field */
  sortBy?: CaseSortField;
  /** Sort direction */
  sortDirection?: SortDirection;
}

export interface SupportCaseStats {
  total: number;
  byStatus: Record<CaseStatus, number>;
  byPriority: Record<CasePriority, number>;
  byChannel: Record<CaseChannel, number>;
  byBranch?: Record<string, number>;
  unassigned: number;
  avgFirstResponseMinutes?: number;
  avgResolutionMinutes?: number;
}

// ============================================================================
// WEBHOOK PAYLOAD TYPES (for external integrations)
// ============================================================================

export interface WebhookCasePayload {
  source: string; // 'jotform', 'call_center', etc.
  sourceId?: string;

  // Required fields
  requesterName: string;
  requesterEmail?: string;
  requesterPhone?: string;
  subject: string;
  body: string;

  // Optional fields
  priority?: CasePriority;
  type?: CaseType;
  tags?: string[];

  // Custom fields from the source
  customFields?: Record<string, string | number | boolean>;
}
